import axios from "axios";

import { IHttpRequestConfig } from "@/browser";
import {
  acknowledgeUpload,
  AcknowledgeUploadETag,
  Attachment,
  requestUpload,
  TAttachmentPurpose,
} from "@/data/attachments";

const FILE_PART_SIZE = 5 * 1024 * 1024;

export interface UploadFileAwsParams {
  file: File;
  type: TAttachmentPurpose;
  parsing?: boolean;
  onUploadProgress?: (progress: number) => void;
}

export const uploadFileAws = async (
  { file, type, parsing, onUploadProgress }: UploadFileAwsParams,
  config?: IHttpRequestConfig,
): Promise<Attachment> => {
  const uploadRequest = await requestUpload(
    {
      type,
      fileName: file.name,
      contentType: file.type,
      systemId: null,
      uploadId: null,
      partNumber: null,
    },
    config,
  );

  const parts = Math.ceil(file.size / FILE_PART_SIZE);
  const eTags: AcknowledgeUploadETag[] = [];

  const freshAxios = axios.create();
  delete freshAxios.defaults.headers.put["Content-Type"];

  for (let partIndex = 0; partIndex < parts; partIndex++) {
    const start = partIndex * FILE_PART_SIZE;
    const end = (partIndex + 1) * FILE_PART_SIZE;
    const part = file.slice(start, end);

    const partUploadRequest = await requestUpload({
      type,
      fileName: file.name,
      contentType: file.type,
      systemId: uploadRequest.systemId,
      uploadId: uploadRequest.uploadId,
      partNumber: partIndex + 1,
    });

    const partUploadResponse = await freshAxios.put(partUploadRequest.url, part, config);

    const eTagHeader = partUploadResponse.headers["etag"] as string;
    const eTag = eTagHeader.substring(1, eTagHeader.length - 1);

    eTags.push({
      eTag,
      partNumber: partIndex + 1,
    });

    const progress = Math.round(((partIndex + 1) * 100) / parts);
    onUploadProgress?.(progress);
  }

  const attachment = await acknowledgeUpload(
    {
      systemId: uploadRequest.systemId,
      uploadId: uploadRequest.uploadId,
      eTags,
      parsing,
    },
    config,
  );

  return attachment;
};
